import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";

import { UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { Upgrade,UpgradeCategory } from "../types";
import Carousel from "./Carousel";
import CollapsableImageContent from "./CollapsableImageContent";
import FullscreenModal from "./FullscreenModal";
import InclusionDetail from "./InclusionDetail";
import Tabs from "./Tabs";

/**
 * Tabbed content shown on the Upgrade and Lifestyle options screens (they are the exact same)
 * @constructor
 */
const UpgradeOptionsTabs = observer(
  (props: {
    categories: UpgradeCategory[];
    upgrades: Upgrade[];
    selectedUpgrades: Map<number, Upgrade> | Map<number, Upgrade[]>;
    setSelectedUpgrade: (categoryId: number, upgrade: Upgrade | null) => void;
    tabIndex?: number; // Optionally control tab index from parent component
    onChangeTabIndex?: (index: number) => void;
    slideIndexes?: number[]; // Optionally slide indexes from parent component
    onChangeSlideIndex?: (tabIndex: number, slideIndex: number) => void;
  }) => {
    const userContext = useContext(UserContext);
    const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number[]>([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalClosing, setIsModalClosing] = useState(false);
    const [modalUpgrade, setModalUpgrade] = useState<Upgrade | null>(null);

    const windowSize = useWindowSize();

    useEffect(() => {
      const slideIndexes = [];
      for (let i = 0; i < props.categories.length; i++) {
        slideIndexes.push(0);
      }
      setCurrentSlideIndex(slideIndexes);
    }, []);

    useEffect(() => {
      if (props.tabIndex !== undefined && props.tabIndex !== currentTabIndex) {
        setCurrentTabIndex(props.tabIndex);
      }
    }, [props.tabIndex]);

    useEffect(() => {
      if (props.slideIndexes !== undefined) {
        setCurrentSlideIndex(props.slideIndexes);
      }
    }, [props.slideIndexes]);

    const onClickMoreDetails = (upgrade: Upgrade) => {
      setModalUpgrade(upgrade);
      setIsModalVisible(true);
    };

    const onModalClose = () => {
      setIsModalVisible(false);
      setIsModalClosing(false);
    };

    const onChangeTabIndex = (index: number) => {
      setCurrentTabIndex(index);
      if (props.onChangeTabIndex) {
        props.onChangeTabIndex(index);
      }
    };

    const onSlideCarousel = (index: number) => {
      const slideIndexes = [...currentSlideIndex];
      slideIndexes[currentTabIndex] = index;
      setCurrentSlideIndex(slideIndexes);

      if (props.onChangeSlideIndex) {
        props.onChangeSlideIndex(currentTabIndex, index);
      }
    };

    const onClickUpgrade = (item: Upgrade) => {
      const existing = props.selectedUpgrades.get(item.category_id);

      if (Array.isArray(existing)) {
        // The parent component will take care of toggling when it is an array (Lifestyle Options)
        props.setSelectedUpgrade(item.category_id, item);
      } else {
        if (existing && existing.id == item.id) {
          props.setSelectedUpgrade(item.category_id, null);
        } else {
          props.setSelectedUpgrade(item.category_id, item);
        }
      }
    };

    const getUpgradeImageOverride = (upgrade: Upgrade) => {
      // Selecting an interior style can override the upgrade image shown
      let image = upgrade.image;
      if (upgrade.image_overrides && userContext.selectedInteriorStyle) {
        if (
          Object.keys(upgrade.image_overrides).indexOf(
            userContext.selectedInteriorStyle.id.toString()
          ) > -1
        ) {
          image = upgrade.image_overrides[userContext.selectedInteriorStyle.id];
        }
      }
      return image;
    };

    const renderTabContent = (tabIndex: number) => {
      const items = props.upgrades.filter((item) => {
        return item.category_id == props.categories[tabIndex].id;
      });

      function isSelected(item: Upgrade) {
        const existing = props.selectedUpgrades.get(item.category_id);
        if (Array.isArray(existing)) {
          return existing.filter((_item) => _item.id === item.id).length > 0;
        } else {
          return !!existing && existing.id == item.id;
        }
      }

      if (windowSize.width <= 992) {
        return (
          <>
            {items.map((item) => {
              return (
                <div key={item.id}>
                  <CollapsableImageContent
                    layout={"upgrade"}
                    image={getUpgradeImageOverride(item)}
                    title={item.title}
                    price={item.price}
                    promo_price={item.promo_price}
                    innerHtml={item.content}
                    selected={isSelected(item)}
                    insetTick={true}
                    onClick={() => onClickUpgrade(item)}
                    onClickMoreDetails={() => onClickMoreDetails(item)}
                    showDetailsAsAccordion={false}
                    details={item}
                  />
                </div>
              );
            })}
          </>
        );
      }

      return (
        <div>
          <Carousel
            key={tabIndex}
            onSlide={onSlideCarousel}
            currentSlideIndex={currentSlideIndex[currentTabIndex]}
            stepSize={2}
            itemWidthPerc={50}
            spacingPx={10}
            items={items.map((item, index) => {
              return (
                <div key={item.id}>
                  <CollapsableImageContent
                    layout={"upgrade"}
                    image={getUpgradeImageOverride(item)}
                    title={item.title}
                    price={item.price}
                    promo_price={item.promo_price}
                    innerHtml={item.content}
                    selected={isSelected(item)}
                    insetTick={index % 2 == 1}
                    onClick={() => onClickUpgrade(item)}
                    onClickMoreDetails={() => onClickMoreDetails(item)}
                    showDetailsAsAccordion={false}
                    details={item}
                  />
                </div>
              );
            })}
          />
        </div>
      );
    };

    return (
      <>
        <Tabs
          headerClassName={"container"}
          titles={props.categories.map((item) => item.title)}
          renderContent={renderTabContent}
          tabIndex={currentTabIndex}
          onChangeTabIndex={(index) => onChangeTabIndex(index)}
        />

        {isModalVisible && modalUpgrade !== null && (
          <FullscreenModal
            title={modalUpgrade.title}
            closing={isModalClosing}
            onClose={onModalClose}
          >
            <InclusionDetail
              inclusion={modalUpgrade}
              onClickBack={() => setIsModalClosing(true)}
              hideBlurb={true}
            />
          </FullscreenModal>
        )}
      </>
    );
  }
);

export default UpgradeOptionsTabs;
